import { ApiError, type AuditEntry, type Group, type InstallerApi, type InstallerGroupsResponse, type Member } from "../cloud/api";
import { FastbootClient, type DeviceVars } from "../fastboot/client";
import { FastbootError, isDisconnect } from "../fastboot/errors";
import type { FastbootTransport } from "../fastboot/transport";
import { affectedPositions, checkAddGuards, checkFirstMember, checkReplaceGuards, effectiveSync, normalizeE164, type GuardFailure } from "../groups/forwarding";
import { hopTestPassed, isHopTestTerminal } from "../groups/hop-test";
import { DownloadError, downloadFile, type ChunkCache } from "../images/download";
import { ManifestError, manifestFiles, totalBytes, verifyManifestEnvelope, type ImageManifest, type ManifestFile, type VerifiedManifest } from "../images/manifest";
import type { ReleaseKey } from "../images/release-keys";
import type { RingLogger } from "../logs/ring";
import { evaluatePreflight, type PreflightResult } from "../models/preflight";
import { FALLBACK_MODELS, type SupportedModel } from "../models/supported";
import type { DeviceProvider } from "./device-provider";
import { simGateOpen, type DeviceInfo, type FlowStore, type GroupInfo, type Intent, type PreflightSummary, type SimReady } from "./machine";

export interface RunnerDeps {
  store: FlowStore;
  api: InstallerApi;
  keys: readonly ReleaseKey[];
  cache: ChunkCache;
  log: RingLogger;
  devices: DeviceProvider;
  fetchFn?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
  /** Timeouts, overridable in tests. */
  rebootTimeoutMs?: number;
  unlockTimeoutMs?: number;
  enrollPollMs?: number;
  jobPollMs?: number;
}

const REBOOT_TIMEOUT = 90_000;
const UNLOCK_TIMEOUT = 5 * 60_000;
const ENROLL_POLL = 3_000;
const JOB_POLL = 2_000;
/** How many enrollment polls to wait for the cloud's final `live` after the chain is verified. */
const LIVE_POLLS = 40;
/** The operator code that erases every forwarding rule on a SIM (any GSM phone). */
export const ERASE_ALL_FORWARDING_CODE = "##002#";

export type GroupSummary = InstallerGroupsResponse["groups"][number];

const toGroupInfo = (g: Pick<Group, "id" | "name" | "main_number" | "members">): GroupInfo => ({ id: g.id, name: g.name, mainNumber: g.main_number, memberCount: g.members.length });

/**
 * Drives the flow: every public method performs one user-visible step, dispatching
 * events into the store and translating failures into `ERROR` events with stable codes
 * (which the UI maps to bilingual messages). Methods are idempotent per step so "Retry"
 * simply calls them again.
 */
/**
 * Shown when the bootloader refuses to unlock. A phone already on QuickHub OS has OEM unlocking forced off by its
 * device owner (anti-theft, #10118) until it is prepared for return in QuickHub, which allows unlocking again.
 */
const UNLOCK_NOT_ALLOWED =
  "bootloader reports unlock is not allowed. A phone that already runs QuickHub OS must be prepared for return " +
  "first by QuickHub support (the phone must be switched on and online), which allows OEM unlocking; otherwise " +
  "OEM unlocking is off or the phone is carrier-locked";

export class InstallRunner {
  private transport: FastbootTransport | undefined;
  private client: FastbootClient | undefined;
  private modelsCache: SupportedModel[] | undefined;
  private groupCache: Group | undefined;
  private polling = false;
  private readonly sleep: (ms: number) => Promise<void>;

  constructor(private readonly d: RunnerDeps) {
    this.sleep = d.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
  }

  get store(): FlowStore {
    return this.d.store;
  }

  // ---- helpers -------------------------------------------------------------------

  private fail(code: string, message: string, retryable = true): void {
    this.d.log.error("flow.error", { code, message, step: this.store.state.step });
    this.store.dispatch({ type: "ERROR", code, message, retryable });
  }

  private guard(g: GuardFailure): false {
    this.fail(`guard_${g.code}`, g.detail, false);
    return false;
  }

  /** Map any thrown thing to an ERROR event. Returns false so callers can `return this.handle(e)`. */
  private handle(e: unknown): false {
    if (e instanceof FastbootError) {
      if (e.code === "disconnected") this.fail("device_disconnected", e.message);
      else if (e.code === "no_interface") this.fail("not_in_fastboot", e.message);
      else if (e.code === "unsupported") this.fail("webusb_unsupported", e.message, false);
      else this.fail(`fastboot_${e.code}`, e.message);
    } else if (e instanceof ManifestError) this.fail(`manifest_${e.code}`, e.message, e.code !== "signature" && e.code !== "google_mismatch");
    else if (e instanceof DownloadError) this.fail(`download_${e.code}`, e.message);
    else if (e instanceof ApiError) this.fail(e.status === 401 ? "unauthenticated" : `api_${e.code}`, e.message, e.status !== 401 && e.status !== 422);
    else if (e instanceof Error && isDisconnect(e)) this.fail("device_disconnected", e.message);
    else if (e instanceof Error && /NotFoundError|No device selected/i.test(e.name + e.message)) this.fail("no_device_selected", e.message);
    else this.fail("unexpected", e instanceof Error ? e.message : String(e));
    return false;
  }

  private async models(): Promise<SupportedModel[]> {
    if (this.modelsCache) return this.modelsCache;
    try {
      this.modelsCache = (await this.d.api.models()).models;
    } catch (e) {
      this.d.log.warn("models.fallback", { error: e });
      this.modelsCache = [...FALLBACK_MODELS];
    }
    return this.modelsCache;
  }

  /** Fresh copy of the selected group (members + forwarding) from the cloud. */
  private async group(id?: string): Promise<Group> {
    const gid = id ?? this.store.state.group?.id;
    if (!gid) throw new Error("no group selected");
    this.groupCache = await this.d.api.getGroup(gid);
    return this.groupCache;
  }

  /** Last group snapshot fetched (UI reads it for member lists without another request). */
  get lastGroup(): Group | undefined {
    return this.groupCache;
  }

  private async attach(t: FastbootTransport): Promise<FastbootClient> {
    if (this.transport && this.transport !== t) await this.transport.close().catch(() => undefined);
    this.transport = t;
    this.client = new FastbootClient(t, { log: (ev, f) => this.d.log.debug(ev, f) });
    return this.client;
  }

  /** After a reboot/unlock/lock, wait for the phone to come back and re-attach. */
  private async reattach(timeoutMs = this.d.rebootTimeoutMs ?? REBOOT_TIMEOUT): Promise<FastbootClient> {
    const serial = this.store.state.device?.serial;
    if (this.transport) await this.transport.close().catch(() => undefined);
    this.transport = undefined;
    this.client = undefined;
    const t = await this.d.devices.waitFor(serial, timeoutMs);
    return this.attach(t);
  }

  private async rebootBootloaderAndWait(fb: FastbootClient): Promise<FastbootClient> {
    await fb.reboot("bootloader");
    return this.reattach();
  }

  private async ensureClient(): Promise<FastbootClient> {
    if (this.client) {
      try {
        await this.client.getvar("product");
        return this.client;
      } catch {
        /* stale handle; reattach below */
      }
    }
    return this.reattach();
  }

  private static toDeviceInfo(v: DeviceVars): DeviceInfo {
    return {
      serial: v.serialno ?? "",
      product: v.product ?? "",
      unlocked: v.unlocked === "yes",
      currentSlot: v.currentSlot,
      antiRollback: v.antiRollback,
      isUserspace: v.isUserspace === "yes",
      versionBootloader: v.versionBootloader,
      versionBaseband: v.versionBaseband,
      unlockAbility: v.unlockAbility,
    };
  }

  private static summary(p: PreflightResult): PreflightSummary {
    return { ok: p.ok, code: p.code, warnings: p.warnings, carrierHint: p.carrierHint, modelName: p.model?.name };
  }

  // ---- step 1: sign-in + line group ---------------------------------------------

  async signIn(devToken?: string): Promise<boolean> {
    try {
      if (devToken !== undefined) await this.d.api.auth.signIn({ token: devToken });
      const me = await this.d.api.me();
      this.d.log.info("auth.signed_in", { business_id: me.business_id });
      this.store.dispatch({ type: "SIGNED_IN", business: { id: me.business_id, name: me.business_name } });
      return true;
    } catch (e) {
      return this.handle(e);
    }
  }

  async signOut(): Promise<void> {
    await this.d.api.auth.signOut();
    this.store.dispatch({ type: "SIGNED_OUT" });
  }

  async listGroups(): Promise<InstallerGroupsResponse | undefined> {
    try {
      return await this.d.api.installerGroups();
    } catch (e) {
      this.handle(e);
      return undefined;
    }
  }

  /** Create an empty group; this phone becomes member 1 holding `mainNumber` once enrolled. */
  async createGroup(name: string, mainNumber: string): Promise<Group | undefined> {
    const n = normalizeE164(mainNumber);
    if (!n) {
      this.guard({ code: "invalid_number", detail: `"${mainNumber}" is not an E.164 number (+9715…)` });
      return undefined;
    }
    try {
      const g = await this.d.api.createGroup({ name: name.trim(), main_number: n });
      this.d.log.info("group.created", { group_id: g.id, main_number: n });
      this.groupCache = g;
      return g;
    } catch (e) {
      this.handle(e);
      return undefined;
    }
  }

  /** Full group detail for the picker (members, forwarding sync) — cached for the UI. */
  async inspectGroup(id: string): Promise<Group | undefined> {
    try {
      return await this.group(id);
    } catch (e) {
      this.handle(e);
      return undefined;
    }
  }

  /**
   * Choose what this phone becomes. Guards mirror the cloud's 422s: a full group cannot take
   * another member, an empty group only takes member 1, a replace needs an existing position.
   */
  async selectGroup(summary: Pick<GroupSummary, "id">, intent: Intent, position?: number): Promise<boolean> {
    try {
      const g = await this.group(summary.id);
      const count = g.members.length;
      let target: number;
      if (intent === "create") {
        if (count > 0) return this.guard({ code: "position_taken", detail: `${g.name} already has ${count} member(s); add a phone instead` });
        target = 1;
      } else if (intent === "add") {
        if (count === 0) intent = "create";
        if (count >= 5) return this.guard({ code: "group_full", detail: `${g.name} already has 5 members (the maximum); replace one instead` });
        target = count + 1;
      } else {
        if (position === undefined || !g.members.some((m) => m.position === position)) return this.guard({ code: "position_unknown", detail: `no member at position ${position ?? "?"} in ${g.name}` });
        target = position;
      }
      this.d.log.info("group.selected", { group_id: g.id, intent, position: target, members: count });
      this.store.dispatch({ type: "GROUP_SELECTED", group: toGroupInfo(g), intent, position: target });
      return true;
    } catch (e) {
      return this.handle(e);
    }
  }

  // ---- step 2: pre-flight --------------------------------------------------------

  /**
   * SIM checklist (D18): the customer confirms a physical SIM is inserted, or that the eSIM
   * profile was activated and tested on stock Android. Required before the phone is read, so
   * the unlock step is unreachable without it; a stock restore needs no SIM.
   */
  confirmSim(sim: SimReady): boolean {
    if (this.store.state.step !== "preflight") {
      this.fail("wrong_step", "the SIM checklist belongs to the pre-flight step", false);
      return false;
    }
    this.d.log.info("preflight.sim_confirmed", { sim });
    this.store.dispatch({ type: "SIM_CONFIRMED", sim });
    return true;
  }

  /** Must run from a click (WebUSB chooser). */
  async connectAndPreflight(): Promise<boolean> {
    if (!simGateOpen(this.store.state)) {
      this.fail("sim_not_confirmed", "confirm the SIM checklist (physical SIM inserted, or eSIM activated on stock Android) first", false);
      return false;
    }
    try {
      const t = await this.d.devices.request();
      let fb = await this.attach(t);
      let vars = await fb.readDeviceVars();
      if (vars.isUserspace === "yes") {
        this.d.log.info("preflight.leaving_fastbootd");
        fb = await this.rebootBootloaderAndWait(fb);
        vars = await fb.readDeviceVars();
      }
      const all = await fb.getvarAll().catch(() => ({}));
      const models = await this.models();
      const result = evaluatePreflight(vars, models, all);
      const device = InstallRunner.toDeviceInfo(vars);
      this.d.log.info("preflight.result", { code: result.code, product: vars.product, serial: vars.serialno, unlocked: vars.unlocked, unlock_ability: vars.unlockAbility, anti_rollback: vars.antiRollback, bootloader: vars.versionBootloader, baseband: vars.versionBaseband, warnings: result.warnings });
      if (!result.ok || !result.model) {
        this.store.dispatch({ type: "DEVICE_REFUSED", device, preflight: InstallRunner.summary(result) });
        return false;
      }
      this.store.dispatch({ type: "DEVICE_READ", device, model: result.model.id, preflight: InstallRunner.summary(result) });
      return true;
    } catch (e) {
      return this.handle(e);
    }
  }

  /** Reload recovery: re-grant USB without redoing preflight (same serial required). */
  async reconnect(): Promise<boolean> {
    try {
      const t = await this.d.devices.request();
      const fb = await this.attach(t);
      const serial = await fb.tryGetvar("serialno");
      const want = this.store.state.device?.serial;
      if (want && serial !== want) {
        this.fail("wrong_device", `expected phone ${want}, got ${serial ?? "?"}`);
        return false;
      }
      return true;
    } catch (e) {
      return this.handle(e);
    }
  }

  // ---- step 3: unlock ------------------------------------------------------------

  confirmUnlock(): void {
    this.store.dispatch({ type: "UNLOCK_CONFIRMED" });
  }

  async unlock(): Promise<boolean> {
    const s = this.store.state;
    if (s.step !== "unlock" || !s.unlockConfirmed) {
      this.fail("unlock_not_confirmed", "data-loss confirmation missing", false);
      return false;
    }
    try {
      const fb = await this.ensureClient();
      const ability = await fb.getUnlockAbility();
      if (ability === false) {
        this.fail("carrier_locked", UNLOCK_NOT_ALLOWED, true);
        return false;
      }
      this.d.log.info("unlock.begin");
      await fb.flashingUnlock(); // the phone now shows its own Yes/No screen
      const fb2 = await this.reattach(this.d.unlockTimeoutMs ?? UNLOCK_TIMEOUT);
      const unlocked = await fb2.tryGetvar("unlocked");
      if (unlocked !== "yes") {
        this.fail("unlock_declined", "phone is still locked (was 'Unlock the bootloader' declined on the phone?)");
        return false;
      }
      this.d.log.info("unlock.done");
      this.store.dispatch({ type: "UNLOCKED" });
      return true;
    } catch (e) {
      if (e instanceof FastbootError && e.code === "fail" && /not allowed|not permitted|denied/i.test(e.reason ?? "")) {
        this.fail("carrier_locked", e.message, true);
        return false;
      }
      return this.handle(e);
    }
  }

  // ---- step 4: flash -------------------------------------------------------------

  private channel(): "quickhub" | "stock" {
    return this.store.state.mode === "stock" ? "stock" : "quickhub";
  }

  private async loadManifest(): Promise<VerifiedManifest> {
    const s = this.store.state;
    const model = s.model;
    if (!model) throw new Error("no model selected");
    const envelope = s.manifest ?? (await this.d.api.manifest(model, this.channel()));
    const v = await verifyManifestEnvelope(envelope, this.d.keys);
    if (v.manifest.model !== model) throw new ManifestError("model_mismatch", `manifest is for ${v.manifest.model}, phone is ${model}`);
    if (v.manifest.channel !== this.channel()) throw new ManifestError("model_mismatch", `manifest channel ${v.manifest.channel} ≠ ${this.channel()}`);
    const arb = s.device?.antiRollback !== undefined ? parseInt(s.device.antiRollback, 10) : NaN;
    if (v.manifest.anti_rollback !== undefined && Number.isFinite(arb) && arb > v.manifest.anti_rollback) {
      throw new ManifestError("model_mismatch", `phone anti-rollback ${arb} is newer than the bundle's ${v.manifest.anti_rollback}`);
    }
    if (!s.manifest) {
      this.store.dispatch({ type: "MANIFEST_READY", envelope, buildId: v.manifest.build_id, fileCount: manifestFiles(v.manifest).length, bytesTotal: totalBytes(v.manifest) });
    }
    this.d.log.info("manifest.verified", { build_id: v.manifest.build_id, key_id: v.keyId, channel: v.manifest.channel });
    return v;
  }

  private cacheKey(m: ImageManifest, f: ManifestFile): string {
    return `${m.channel}/${m.model}/${m.build_id}/${f.file}`;
  }

  private fetchImage(v: VerifiedManifest, f: ManifestFile, onProgress?: (done: number) => void): Promise<Uint8Array> {
    return downloadFile({
      url: `${v.baseUrl}${f.file}`,
      size: f.size,
      sha256: f.sha256,
      cacheKey: this.cacheKey(v.manifest, f),
      cache: this.d.cache,
      ...(this.d.fetchFn ? { fetchFn: this.d.fetchFn } : {}),
      ...(onProgress ? { onProgress } : {}),
    });
  }

  /** Phase: download everything into the chunk cache (resumable), verifying each sha256. */
  private async downloadAll(v: VerifiedManifest): Promise<void> {
    const files = manifestFiles(v.manifest);
    let before = 0;
    for (let i = 0; i < files.length; i++) {
      const { what, entry } = files[i]!;
      this.store.dispatch({ type: "DOWNLOAD_PROGRESS", fileIndex: i, current: what, bytesDone: before });
      const base = before;
      await this.fetchImage(v, entry, (done) => this.store.dispatch({ type: "DOWNLOAD_PROGRESS", fileIndex: i, current: what, bytesDone: base + done }));
      before += entry.size;
      this.d.log.info("download.file_ok", { file: entry.file, bytes: entry.size });
    }
  }

  /** Phase: write the phone. Re-reads each file from the cache and re-verifies sha256 right before flashing. */
  private async flashAll(v: VerifiedManifest): Promise<void> {
    const m = v.manifest;
    let fb = await this.ensureClient();
    if ((await fb.tryGetvar("unlocked")) !== "yes") throw new FastbootError("fail", "bootloader is locked", { reason: "locked" });
    const max = await fb.getMaxDownloadSize();
    let idx = 0;
    const flashOne = async (what: string, partition: string, entry: ManifestFile) => {
      const img = await this.fetchImage(v, entry);
      this.store.dispatch({ type: "FLASH_PROGRESS", fileIndex: idx, current: what, partDone: 0, partTotal: entry.size });
      await fb.flash(partition, img, (p) => this.store.dispatch({ type: "FLASH_PROGRESS", fileIndex: idx, current: what, partDone: p.sent, partTotal: p.total }), max);
      idx++;
    };

    // 1. Google's bootloader, then reboot into the new bootloader.
    this.store.dispatch({ type: "FLASH_PHASE", phase: "flashing", current: "bootloader" });
    await flashOne("bootloader", "bootloader", m.bootloader);
    fb = await this.rebootBootloaderAndWait(fb);
    // 2. Google's radio, then reboot again.
    await flashOne("radio", "radio", m.radio);
    fb = await this.rebootBootloaderAndWait(fb);
    const blVer = await fb.tryGetvar("version-bootloader");
    const bbVer = await fb.tryGetvar("version-baseband");
    this.d.log.info("flash.google_images_done", { bootloader: blVer, baseband: bbVer, expected_bootloader: m.bootloader.version, expected_radio: m.radio.version });
    // 3. Bootloader-mode partitions in manifest order.
    for (const img of m.images.filter((i) => (i.flash_via ?? "bootloader") === "bootloader")) await flashOne(img.partition, img.partition, img);
    // 4. Logical partitions via fastbootd, if any.
    const userspace = m.images.filter((i) => i.flash_via === "fastbootd");
    if (userspace.length > 0) {
      await fb.reboot("fastboot");
      fb = await this.reattach();
      if ((await fb.tryGetvar("is-userspace")) !== "yes") throw new FastbootError("protocol", "expected fastbootd after reboot fastboot");
      for (const img of userspace) await flashOne(img.partition, img.partition, img);
      fb = await this.rebootBootloaderAndWait(fb);
    }
    // 5. Wipe.
    for (const p of m.erase ?? ["userdata", "metadata"]) {
      this.store.dispatch({ type: "FLASH_PROGRESS", fileIndex: idx, current: `erase:${p}`, partDone: 0, partTotal: 0 });
      await fb.erase(p);
    }
    // 6. Verified-boot key.
    await fb.erase("avb_custom_key").catch((e: unknown) => this.d.log.warn("avb_custom_key.erase_failed", { error: e }));
    if (m.avb_custom_key) await flashOne("avb_custom_key", "avb_custom_key", m.avb_custom_key);
  }

  private async lockAndReboot(): Promise<void> {
    const s = this.store.state;
    let fb = await this.ensureClient();
    this.store.dispatch({ type: "FLASH_PHASE", phase: "locking" });
    if (!s.leaveUnlocked) {
      this.d.log.info("lock.begin");
      await fb.flashingLock(); // phone asks for confirmation, then reboots to bootloader
      fb = await this.reattach(this.d.unlockTimeoutMs ?? UNLOCK_TIMEOUT);
      const unlocked = await fb.tryGetvar("unlocked");
      if (unlocked !== "no") throw new FastbootError("fail", "bootloader is still unlocked after 'flashing lock' (declined on the phone?)", { reason: "lock_declined" });
      this.d.log.info("lock.done");
    }
    await fb.reboot();
    if (this.transport) await this.transport.close().catch(() => undefined);
    this.transport = undefined;
    this.client = undefined;
    this.store.dispatch({ type: "LOCKED" });
  }

  /**
   * Runs the whole flash step from wherever the persisted state left off:
   * manifest → download → flashing → locking. Safe to call again after an error.
   */
  async flash(): Promise<boolean> {
    if (this.store.state.step !== "flash") {
      this.fail("wrong_step", `flash() called in step ${this.store.state.step}`, false);
      return false;
    }
    try {
      const v = await this.loadManifest();
      const phase = this.store.state.flash?.phase ?? "manifest";
      if (phase === "manifest" || phase === "download") {
        this.store.dispatch({ type: "FLASH_PHASE", phase: "download" });
        await this.downloadAll(v);
      }
      if (phase !== "locking" && phase !== "done") await this.flashAll(v);
      if (phase !== "done") await this.lockAndReboot();
      return true;
    } catch (e) {
      return this.handle(e);
    }
  }

  // ---- step 5: enroll ------------------------------------------------------------

  async requestEnrollmentToken(): Promise<boolean> {
    const s = this.store.state;
    if (s.step !== "enroll" || !s.group || !s.model || !s.device || s.position === undefined) {
      this.fail("wrong_step", "enroll() before flash completed", false);
      return false;
    }
    try {
      const tok = await this.d.api.createEnrollmentToken({ group_id: s.group.id, position: s.position, model: s.model, serialno: s.device.serial });
      this.d.log.info("enroll.token_issued", { enrollment_id: tok.enrollment_id, expires_at: tok.expires_at, group_id: s.group.id, position: s.position });
      this.store.dispatch({ type: "ENROLLMENT_TOKEN", enrollment: { id: tok.enrollment_id, token: tok.token, qrPayload: tok.qr_payload, expiresAt: tok.expires_at } });
      return true;
    } catch (e) {
      return this.handle(e);
    }
  }

  /**
   * Poll until the phone is enrolled and capability-probed (the gate to the member step),
   * or expired / failed. Resolves true once `probed` (or `live`) is reached.
   */
  async pollEnrollment(maxPolls = Number.POSITIVE_INFINITY): Promise<boolean> {
    if (this.polling) return false;
    this.polling = true;
    try {
      let n = 0;
      while (n++ < maxPolls) {
        const s = this.store.state;
        if (s.step !== "enroll" || !s.enrollment) return false;
        const st = await this.d.api.enrollmentStatus(s.enrollment.id);
        if (st.state !== s.enrollment.state) this.d.log.info("enroll.state", { state: st.state, device_id: st.device_id, forwarding_sync: st.forwarding_sync });
        this.store.dispatch({ type: "ENROLLMENT_STATE", state: st.state, capabilities: st.capabilities, deviceId: st.device_id, simNumber: st.sim_number ?? null });
        if (st.state === "probed" || st.state === "live") return true;
        if (st.state === "expired") {
          this.fail("enrollment_expired", "the enrollment token expired before the phone used it");
          return false;
        }
        if (st.state === "failed") {
          this.fail("enrollment_failed", st.error ?? "enrollment failed");
          return false;
        }
        await this.sleep(this.d.enrollPollMs ?? ENROLL_POLL);
      }
      return false;
    } catch (e) {
      return this.handle(e);
    } finally {
      this.polling = false;
    }
  }

  // ---- step 5b: join the group (add / create / replace) + chain progress ----------

  /** The number the member form should start with: the cloud's SIM read-back, else the group's main number for member 1, else the replaced member's number. */
  prefillNumber(): string {
    const s = this.store.state;
    if (s.enrollment?.simNumber) return s.enrollment.simNumber;
    if (s.intent === "create") return s.group?.mainNumber ?? "";
    if (s.intent === "replace" && s.position !== undefined) return this.groupCache?.members.find((m) => m.position === s.position)?.number ?? "";
    return "";
  }

  /**
   * Add this phone to the group (or replace the member at the chosen position), then follow
   * the cloud's reprogramming job until both affected phones read back the desired state.
   */
  async submitMember(input: { number: string; attested: boolean; attestedBy: string }): Promise<boolean> {
    const s = this.store.state;
    if (s.step !== "member" || !s.group || !s.enrollment?.deviceId || !s.intent || s.position === undefined) {
      this.fail("wrong_step", "the phone must be enrolled and capability-probed before joining the group", false);
      return false;
    }
    const deviceId = s.enrollment.deviceId;
    try {
      const g = await this.group();
      const candidate = { number: input.number, deviceId, attested: input.attested, attestedBy: input.attestedBy };
      let position: number;
      let jobId: string | undefined;
      let number = normalizeE164(input.number) ?? input.number;
      if (s.intent === "replace") {
        const gf = checkReplaceGuards(g, s.position, candidate);
        if (gf) return this.guard(gf);
        const old = g.members.find((m) => m.position === s.position)!;
        const res = await this.d.api.replaceMember(g.id, s.position, { device_id: deviceId, ...(number !== old.number ? { number } : {}) });
        position = res.position;
        number = res.number;
        jobId = res.job_id;
        this.d.log.info("group.member_replaced", { group_id: g.id, position, number_changed: number !== old.number });
      } else {
        const gf = s.intent === "create" ? (checkFirstMember(g, input.number) ?? (candidate.attested && candidate.attestedBy.trim() ? undefined : checkAddGuards({ members: [], main_number: g.main_number }, candidate))) : checkAddGuards(g, candidate);
        if (gf) return this.guard(gf);
        const res = await this.d.api.addMember(g.id, { number, licence_attested: true, attested_by: input.attestedBy.trim(), device_id: deviceId, enrollment_id: s.enrollment.id });
        position = res.position;
        number = res.number;
        jobId = res.job_id;
        this.d.log.info("group.member_added", { group_id: g.id, position, attested_by: input.attestedBy.trim() });
      }
      if (!jobId) jobId = (await this.d.api.reconcile(g.id)).job_id; // contract gap: see PROTOCOL_GAPS.md
      const after = await this.group();
      // A replace only touches the predecessor when the number changed (forwarding targets the number).
      const affected = s.intent === "replace" ? (number !== g.members.find((m) => m.position === position)?.number ? [position - 1, position].filter((p) => p >= 1) : [position]) : affectedPositions(after.members, position);
      this.store.dispatch({ type: "MEMBER_JOB_STARTED", position, number, attestedBy: input.attestedBy.trim(), jobId, affected });
      return await this.pollMemberJob();
    } catch (e) {
      return this.handle(e);
    }
  }

  /** Re-run reconciliation after `forwarding_out_of_sync` (or a failed job) and follow it again. */
  async repairChain(): Promise<boolean> {
    const s = this.store.state;
    if (s.step !== "member" || !s.member || !s.group) {
      this.fail("wrong_step", "nothing to repair", false);
      return false;
    }
    try {
      const { job_id } = await this.d.api.reconcile(s.group.id);
      this.store.dispatch({ type: "MEMBER_JOB_STARTED", position: s.member.position, number: s.member.number, attestedBy: s.member.attestedBy, jobId: job_id, affected: s.member.affected });
      return await this.pollMemberJob();
    } catch (e) {
      return this.handle(e);
    }
  }

  /** Follow `GET /groups/{id}/jobs/{job_id}` until done, then fetch the read-backs. */
  async pollMemberJob(maxPolls = Number.POSITIVE_INFINITY): Promise<boolean> {
    if (this.polling) return false;
    this.polling = true;
    try {
      let n = 0;
      while (n++ < maxPolls) {
        const s = this.store.state;
        if (s.step !== "member" || !s.member || !s.group) return false;
        const job = await this.d.api.groupJob(s.group.id, s.member.jobId);
        if (job.state !== s.member.job?.state) this.d.log.info("group.job", { job_id: s.member.jobId, state: job.state, steps: job.steps.map((st) => `${st.member_position}:${st.action}:${st.state}`) });
        this.store.dispatch({ type: "MEMBER_JOB_STATE", job });
        if (job.state === "failed") {
          this.fail("chain_job_failed", job.error ?? job.steps.find((st) => st.state === "failed")?.detail ?? "the cloud could not reprogram the chain");
          return false;
        }
        if (job.state === "done") {
          const g = await this.group();
          const affected = g.members.filter((m) => s.member!.affected.includes(m.position));
          const bad = affected.filter((m) => effectiveSync(m) === "out_of_sync");
          this.d.log.info("group.readback", { members: affected.map((m) => ({ position: m.position, sync: effectiveSync(m), source: m.forwarding.actual?.source })) });
          if (bad.length > 0) {
            this.fail("forwarding_out_of_sync", `member ${bad.map((m) => m.position).join(", ")} reads back a forwarding state that does not match the desired chain`);
            return false;
          }
          this.store.dispatch({ type: "MEMBER_READBACK", members: g.members });
          return true;
        }
        await this.sleep(this.d.jobPollMs ?? JOB_POLL);
      }
      return false;
    } catch (e) {
      return this.handle(e);
    } finally {
      this.polling = false;
    }
  }

  // ---- step 5c: live hop test ----------------------------------------------------

  /** Mandatory when any affected member reported `source:"unverified"`; otherwise optional. */
  async startHopTest(callerNumber: string, testNoReply = false): Promise<boolean> {
    const s = this.store.state;
    if (s.step !== "hoptest" || !s.hopTest || !s.group || !s.member?.members) {
      this.fail("wrong_step", "hop test before the chain was programmed", false);
      return false;
    }
    const caller = normalizeE164(callerNumber);
    if (!caller) return this.guard({ code: "invalid_number", detail: `"${callerNumber}" is not an E.164 number` });
    if (s.member.members.some((m) => m.number === caller)) return this.guard({ code: "loop", detail: "call from a phone that is not a member of the group" });
    try {
      const hops = s.member.members.map((m) => m.position).sort((a, b) => a - b);
      const { job_id } = await this.d.api.startHopTest(s.group.id, { caller_number: caller, hops, test_no_reply: testNoReply });
      this.d.log.info("hop_test.started", { job_id, hops, test_no_reply: testNoReply });
      this.store.dispatch({ type: "HOP_TEST_STARTED", jobId: job_id, callerNumber: caller });
      return await this.pollHopTest();
    } catch (e) {
      return this.handle(e);
    }
  }

  async pollHopTest(maxPolls = Number.POSITIVE_INFINITY): Promise<boolean> {
    if (this.polling) return false;
    this.polling = true;
    try {
      let n = 0;
      while (n++ < maxPolls) {
        const s = this.store.state;
        if (s.step !== "hoptest" || !s.hopTest?.jobId || !s.group) return false;
        const r = await this.d.api.hopTest(s.group.id, s.hopTest.jobId);
        this.store.dispatch({ type: "HOP_TEST_STATE", result: r });
        if (isHopTestTerminal(r)) {
          const passed = hopTestPassed(r);
          this.d.log.info("hop_test.result", { state: r.state, passed, steps: r.steps, no_reply: r.no_reply });
          this.store.dispatch({ type: "HOP_TEST_AUDIT", entry: await this.latestHopTestAudit(s.group.id) });
          if (!passed) {
            const wrong = r.steps.filter((st) => !st.ok).map((st) => `hop ${st.hop}: expected member ${st.expected_member}, ${st.rang_member === null ? "nothing rang" : `member ${st.rang_member} rang`}`);
            this.fail("hop_test_failed", r.error ?? (wrong.length ? wrong.join("; ") : "no-reply forwarding did not reach the next member"));
            return false;
          }
          return true;
        }
        await this.sleep(this.d.jobPollMs ?? JOB_POLL);
      }
      return false;
    } catch (e) {
      return this.handle(e);
    } finally {
      this.polling = false;
    }
  }

  private async latestHopTestAudit(groupId: string): Promise<AuditEntry | undefined> {
    try {
      const entries = await this.d.api.groupAudit(groupId);
      return [...entries].reverse().find((a) => /hop_test/i.test(a.action)) ?? entries.at(-1);
    } catch (e) {
      this.d.log.warn("hop_test.audit_unavailable", { error: e });
      return undefined;
    }
  }

  skipHopTest(): boolean {
    const s = this.store.state;
    if (s.step !== "hoptest" || !s.hopTest) {
      this.fail("wrong_step", "no hop test to skip", false);
      return false;
    }
    if (s.hopTest.required) {
      this.fail("hop_test_required", "a member read back an unverified forwarding state; the live hop test is mandatory", false);
      return false;
    }
    this.d.log.info("hop_test.skipped");
    this.store.dispatch({ type: "HOP_TEST_SKIPPED" });
    return true;
  }

  /** After the hop test (or skipping it), wait for the cloud's final `live` verdict on the enrollment. */
  async completeInstall(maxPolls = LIVE_POLLS): Promise<boolean> {
    const s = this.store.state;
    if (s.step !== "hoptest" || !s.hopTest || !(s.hopTest.skipped || s.hopTest.passed) || !s.enrollment) {
      this.fail("wrong_step", "the hop test has not passed or been skipped", false);
      return false;
    }
    try {
      for (let n = 0; n < maxPolls; n++) {
        const st = await this.d.api.enrollmentStatus(s.enrollment.id);
        this.store.dispatch({ type: "ENROLLMENT_STATE", state: st.state, capabilities: st.capabilities, deviceId: st.device_id, simNumber: st.sim_number ?? null });
        if (st.state === "live") {
          this.d.log.info("install.live", { device_id: st.device_id, position: st.position });
          this.store.dispatch({ type: "INSTALL_LIVE" });
          return true;
        }
        if (st.state === "expired" || st.state === "failed") {
          this.fail("enrollment_failed", st.error ?? `enrollment ended in state ${st.state}`);
          return false;
        }
        await this.sleep(this.d.enrollPollMs ?? ENROLL_POLL);
      }
      this.fail("not_live_yet", "the cloud has not marked the line live yet (forwarding sync pending); retry in a moment");
      return false;
    } catch (e) {
      return this.handle(e);
    }
  }

  // ---- step 6: recovery ----------------------------------------------------------

  /**
   * Reflash needs the member's position (it re-enrolls as a replace with the same SIM). A stock
   * restore of a phone that is still an enrolled member is refused: run Remove first so the
   * chain is re-pointed and its forwarding erased (Part A2b "Removal matters").
   */
  async startRecovery(summary: Pick<GroupSummary, "id">, mode: "reflash" | "stock", leaveUnlocked: boolean, position?: number): Promise<boolean> {
    try {
      const g = await this.group(summary.id);
      const member = position !== undefined ? g.members.find((m) => m.position === position) : undefined;
      if (mode === "reflash" && !member) return this.guard({ code: "position_unknown", detail: `no member at position ${position ?? "?"} to reflash` });
      if (mode === "stock" && member?.device_id) {
        this.fail("member_not_removed", `member ${member.position} (${member.number}) is still enrolled; remove it from the group before wiping the phone`, false);
        return false;
      }
      this.d.log.info("recovery.start", { mode, leave_unlocked: leaveUnlocked, group_id: g.id, position });
      this.store.dispatch({ type: "START_RECOVERY", group: toGroupInfo(g), mode, leaveUnlocked: mode === "stock" && leaveUnlocked, position });
      return true;
    } catch (e) {
      return this.handle(e);
    }
  }

  // ---- remove a member (no phone needed) -----------------------------------------

  /**
   * Ordered removal: the cloud re-points the predecessor past the member (or makes it the new
   * last member with call waiting on), then erases the removed member's forwarding, then waits
   * for read-backs. `force` is for a dead phone: its forwarding cannot be erased by the phone,
   * so the customer dials `##002#` on that SIM and confirms.
   */
  async startRemoval(summary: Pick<GroupSummary, "id">, position: number, force: boolean): Promise<boolean> {
    try {
      const g = await this.group(summary.id);
      if (!g.members.some((m) => m.position === position)) return this.guard({ code: "position_unknown", detail: `no member at position ${position} in ${g.name}` });
      this.store.dispatch({ type: "START_REMOVAL", group: toGroupInfo(g), position, force });
      const res = await this.d.api.removeMember(g.id, position, force);
      this.d.log.info("group.remove_started", { group_id: g.id, position, force, job_id: res.job_id });
      this.store.dispatch({ type: "REMOVAL_JOB", jobId: res.job_id, ...(force ? { clearInstruction: res.clear_instruction ?? ERASE_ALL_FORWARDING_CODE } : {}) });
      return await this.pollRemovalJob();
    } catch (e) {
      return this.handle(e);
    }
  }

  async pollRemovalJob(maxPolls = Number.POSITIVE_INFINITY): Promise<boolean> {
    if (this.polling) return false;
    this.polling = true;
    try {
      let n = 0;
      while (n++ < maxPolls) {
        const s = this.store.state;
        if (s.step !== "remove" || !s.removal?.jobId) return false;
        const job = await this.d.api.groupJob(s.removal.group.id, s.removal.jobId);
        if (job.state !== s.removal.job?.state) this.d.log.info("group.job", { job_id: s.removal.jobId, state: job.state, steps: job.steps.map((st) => `${st.member_position}:${st.action}:${st.state}`) });
        this.store.dispatch({ type: "REMOVAL_JOB_STATE", job });
        if (job.state === "failed") {
          this.fail("chain_job_failed", job.error ?? job.steps.find((st) => st.state === "failed")?.detail ?? "the cloud could not reprogram the chain");
          return false;
        }
        if (job.state === "done") {
          await this.group(s.removal.group.id).catch(() => undefined);
          if (!s.removal.force) this.store.dispatch({ type: "REMOVAL_DONE" });
          return true;
        }
        await this.sleep(this.d.jobPollMs ?? JOB_POLL);
      }
      return false;
    } catch (e) {
      return this.handle(e);
    } finally {
      this.polling = false;
    }
  }

  /** Dead-phone path: the customer has dialled `##002#` on the removed SIM. */
  async confirmCleared(): Promise<boolean> {
    const s = this.store.state;
    if (s.step !== "remove" || !s.removal?.force || s.removal.job?.state !== "done") {
      this.fail("wrong_step", "nothing to confirm", false);
      return false;
    }
    try {
      await this.d.api.confirmCleared(s.removal.group.id, s.removal.position);
      this.d.log.info("group.remove_cleared_confirmed", { group_id: s.removal.group.id, position: s.removal.position });
      this.store.dispatch({ type: "REMOVAL_CLEARED_CONFIRMED" });
      this.store.dispatch({ type: "REMOVAL_DONE" });
      return true;
    } catch (e) {
      return this.handle(e);
    }
  }

  restartWithAnotherPhone(): void {
    this.store.dispatch({ type: "RESTART_DEVICE" });
  }

  retry(): void {
    this.store.dispatch({ type: "RETRY" });
  }

  /** Members of the last fetched group snapshot (for UI labels). */
  members(): Member[] {
    return this.groupCache?.members ?? [];
  }

  // ---- logs ----------------------------------------------------------------------

  async uploadLogs(userAgent: string): Promise<string | undefined> {
    const s = this.store.state;
    try {
      const r = await this.d.api.uploadSupportLogs({ session_id: s.sessionId, model: s.model ?? null, serialno: s.device?.serial ?? null, user_agent: userAgent, entries: this.d.log.snapshot() });
      this.d.log.info("logs.uploaded", { ticket_id: r.ticket_id });
      return r.ticket_id;
    } catch (e) {
      this.handle(e);
      return undefined;
    }
  }
}
